import React, { useEffect, useRef } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItemCategory } from "../itemcategory/itemCategoryModel";
import { IItem } from "../item/itemModel";
import FormTextArea from "../../components/formFields/FormTextArea";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import JadaiReceiptUsedByKarigarItemsArray from "./JadaiReceiptUsedByKarigarItemsArray";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import AppDialog, { CustomAppDialog } from "../../components/dialog/Dialog";
import { ImCross } from "react-icons/im";
import { closeDialog } from "../../components/dialog/dialogSlice";

import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  closeJadaiReceiptChecklistDialog,
  closeJadaiReceiptViewImageDialog,
  openJadaiReceiptChecklistDialog,
  openJadaiReceiptOrderSelectionDialog,
  openJadaiReceiptViewImageDialog,
  setJadaiReceiptOrderItemIndex,
  setJadaiReceiptOrderSave,
  setJadaiReceiptPreviousOrder,
} from "./jadaiReceiptSlice";
import OrderSelectionDialog from "./OrderSelectionDialog";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { useLocation } from "react-router-dom";
import Slider from "react-slick";
import AlertBox from "../../components/common/AlertBox";
import { setOrderImageViewPayload } from "../order/orderSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import { FaRegEye } from "react-icons/fa";
import { PROCESS_ID } from "../../_contstants/common";
import { findProductImageByProcess } from "../item/services/item.services";
import { findByOrderNoJadaiReceipt } from "./services/jadaireceipt.services";
import moment from "moment";
import { formatWeight } from "../../_helper/helper";

interface ChangeWatchersProps {
  formRenderProps: FormRenderProps;
  fieldname: string;
}

const JadaiReceiptWatcher: React.FC<ChangeWatchersProps> = ({
  fieldname,
  formRenderProps,
}) => {
  const unitList = useAppSelector((state) => state.unit.UnitList);
  const itemArray = formRenderProps.valueGetter(
    `${fieldname}.item_used_by_kariagr`,
  );

  const jadai_receipt_order = formRenderProps.valueGetter(
    "jadai_receipt_order",
  );

  const stone_qty = formRenderProps.valueGetter(`${fieldname}.stone_qty`) || 0;
  const rate = formRenderProps.valueGetter(`${fieldname}.rate`) || 0;
  const extra_cost =
    formRenderProps.valueGetter(`${fieldname}.extra_cost`) || 0;

  useEffect(() => {
    let stoneWeight = 0;
    let subTotal = 0;

    itemArray?.forEach((item: any, idx: number) => {
      const unit = unitList?.find((u) => u.id === item.unit_id);

      stoneWeight += unit?.conversion_rate
        ? unit.conversion_rate * item.weight
        : item.weight || 0;

      const perTotal =
        item.apply_on === "P"
          ? (item.qty || 0) * (item.rate || 0)
          : (item.weight || 0) * (item.rate || 0);

      formRenderProps.onChange(
        `${fieldname}.item_used_by_kariagr.${idx}.total`,
        {
          value: perTotal,
        },
      );

      subTotal += perTotal;
    });

    stoneWeight &&
      formRenderProps.onChange(`${fieldname}.stone_weight`, {
        value: stoneWeight,
      });

    const total = subTotal + stone_qty * rate + extra_cost;
    total &&
      formRenderProps.onChange(`${fieldname}.total`, {
        value: total,
      });
  }, [
    jadai_receipt_order?.map((item: any) => item.gross_weight).join("-"),
    jadai_receipt_order?.map((item: any) => item.stone_weight).join("-"),
    jadai_receipt_order?.map((item: any) => item.purity).join("-"),
    jadai_receipt_order?.map((item: any) => item.wastage).join("-"),
    jadai_receipt_order?.map((item: any) => item.weight).join("-"),
    jadai_receipt_order?.map((item: any) => item.kundan).join("-"),
    itemArray
      ?.map((i: any) => [
        i.weight,
        i.qty,
        i.rate,
        i.apply_on,
        i.unit_id,
        i.total,
      ])
      .join("|"),
    stone_qty,
    rate,
    extra_cost,
  ]);

  return null;
};

const JadaiReceiptOrderItemEntryArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const jadai_receipt_guid = location?.state?.jadai_receipt_guid;
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.jadaireceipt.loading);
  const gridRef = useRef<any>(null);
  const JadaiReceiptOrderItemIndex = useAppSelector(
    (state) => state.jadaireceipt.JadaiReceiptOrderItemIndex,
  );
  const JadaiReceiptPreviousOrder = useAppSelector(
    (state) => state.jadaireceipt.JadaiReceiptPreviousOrder,
  );
  const JadaiReceiptChecklistDialog = useAppSelector(
    (state) => state.jadaireceipt.JadaiReceiptChecklistDialog,
  );
  const formRenderProps = fieldArrayRenderProps.formRenderProps;

  const currentIndex =
    JadaiReceiptOrderItemIndex === 999
      ? fieldArrayRenderProps.value?.length === 0
        ? 0
        : fieldArrayRenderProps.value?.length - 1
      : JadaiReceiptOrderItemIndex;

  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const ItemCategoryList = useAppSelector(
    (state) => state.itemCategory.ItemCategoryList,
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        order_id: null,
        order_no: null,
        item_id: null,
        item_category_id: null,
        item_description: "",
        gross_weight: null,
        ForOpenSettings: false,
        Dank: null,
        stone_weight: null,
        net_weight: null,
        // purity: null,
        // wastage: null,
        // phine: null,
        stone_qty: null,
        rate: null,
        extra_cost: null,
        total: null,
        kundan: null,
        less_jadai_verification: null,
        item_used_by_kariagr: [],
      },
    });
  }, [fieldArrayRenderProps]);

  useEffect(() => {
    if (JadaiReceiptOrderItemIndex === 999) {
      pushElementInarray();
    }
  }, [JadaiReceiptOrderItemIndex]);

  const handleFormClose = (index: any) => {
    if (JadaiReceiptOrderItemIndex === 999) {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      dispatch(setJadaiReceiptOrderItemIndex(-1));
      dispatch(setJadaiReceiptOrderSave(false));
    } else {
      dispatch(closeDialog());
      dispatch(setJadaiReceiptOrderItemIndex(-1));
      dispatch(setJadaiReceiptOrderSave(false));
      fieldArrayRenderProps?.formRenderProps.onChange(`${fieldName}.${index}`, {
        value: JSON.parse(JadaiReceiptPreviousOrder),
      });
      dispatch(setJadaiReceiptPreviousOrder(null));
    }
  };

  // const handleAddOrder = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   const isValid = hasRequired(fieldArrayRenderProps.formRenderProps.errors, [
  //     `jadai_receipt_order.${currentIndex}.item_used_by_kariagr.`,
  //     `jadai_receipt_order.${currentIndex}.order_no`,
  //     `jadai_receipt_order.${currentIndex}.gross_weight`,
  //     `jadai_receipt_order.${currentIndex}.item_category_id`,
  //     `jadai_receipt_order.${currentIndex}.item_id`,
  //   ]);

  //   await fieldArrayRenderProps.formRenderProps.onSubmit(e);
  //   if (isValid) {
  //     dispatch(closeDialog());
  //     dispatch(setJadaiReceiptOrderItemIndex(null));
  //     dispatch(setJadaiReceiptOrderSave(false));
  //   }
  // };

  const handleAddOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValidate =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.order_no &&
      fieldArrayRenderProps.value[currentIndex]?.item_category_id &&
      fieldArrayRenderProps.value[currentIndex]?.item_id &&
      fieldArrayRenderProps.value[currentIndex]?.net_weight;

    // const isValidApprovalItems =
    //   (currentIndex !== null &&
    //     (fieldArrayRenderProps.value[currentIndex].approval_items?.length ===
    //       0 ||
    //       fieldArrayRenderProps.value[currentIndex].approval_items?.every(
    //         (item: any) =>
    //           item?.type &&
    //           item?.item_id &&
    //           item?.weight &&
    //           item?.qty &&
    //           item?.unit_id,
    //       ))) ??
    //   false;
    const isValidKarigarItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr
          .length === 0 ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr.every(
          (item: any) =>
            item.item_id &&
            item.weight &&
            item.qty &&
            item.unit_id &&
            item.apply_on &&
            item.rate,
        ));
    await fieldArrayRenderProps.formRenderProps.onSubmit(e);
    if (isValidate && isValidKarigarItems) {
      if (fieldArrayRenderProps.value[currentIndex]?.id) {
        const getpayload = {
          order_id: fieldArrayRenderProps.value[currentIndex]?.order_id,
          jadai_issue_id:
            fieldArrayRenderProps.value[currentIndex]?.jadai_issue_id,
        };
        const { payload: res, meta } = await dispatch(
          findByOrderNoJadaiReceipt(getpayload),
        );
        if (meta.requestStatus === "fulfilled" && res) {
          const updateField = (field: any, value: any) =>
            formRenderProps?.onChange(`${fieldName}.${currentIndex}.${field}`, {
              value,
            });
          updateField("item_id", res?.item_id);
          updateField("item_name", res?.item_name);
          updateField("order_id", res?.order_id);
          updateField("order_no", res?.order_no);
          updateField("ghat_id", res?.ghat_id);
          updateField("item_description", res?.order_process_description);
          updateField("Order_remark", res?.order_vepari_remark);
          updateField("issue_remarks", res?.order_issue_remarks);
          updateField("item_category_id", res?.item_category_id);
          updateField("weight", res?.weight);
          updateField("with_without_variation", res?.with_without_variation);
          updateField("purity_id", res?.purity_id);
          updateField("item_image", res?.item_image);
          updateField("order_date", res?.order_date);
          updateField("delivery_date", res?.delivery_date);
          updateField("product_name", res?.product_name);
          updateField("category_name", res?.category_name);
          updateField("sub_category_name", res?.sub_category_name);
          updateField("order_estimate_weight", res?.order_estimate_weight);
          updateField("size", res?.size);
          updateField("purity", res?.purity);
          updateField("Carat", res?.Carat);
          updateField("color", res?.color);
          updateField("priority", res?.priority);
          updateField("karigar_name", res?.karigar_name);
          updateField("given_date", res?.given_date);
          updateField("receive_date", res?.receive_date);
          updateField(
            "sizedetails",
            JSON.parse(JSON.stringify(res?.sizedetails))?.map((item: any) => ({
              title: item.title,
              value: item.value,
            })),
          );
          dispatch(openJadaiReceiptChecklistDialog());
        } else {
          const updateField = (field: any, value: any) =>
            formRenderProps?.onChange(field, { value });
          updateField("item_id", null);
          updateField("item_name", "");
          updateField("order_id", null);
          updateField("order_no", null);
          updateField("ghat_id", null);
          updateField("item_description", "");
          updateField("item_category_id", null);
          updateField("weight", null);
          updateField("purity_id", null);
          updateField("item_image", null);
          updateField("sizedetails", []);
        }
      } else {
        dispatch(openJadaiReceiptChecklistDialog());
      }
    }
  };

  const handleCloseAndAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValidate =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.order_no &&
      fieldArrayRenderProps.value[currentIndex]?.item_category_id &&
      fieldArrayRenderProps.value[currentIndex]?.item_id &&
      fieldArrayRenderProps.value[currentIndex]?.gross_weight;

    const isChecklistApproved =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.iscaret &&
      fieldArrayRenderProps.value[currentIndex]?.isdesign &&
      fieldArrayRenderProps.value[currentIndex]?.isgoldcolor &&
      fieldArrayRenderProps.value[currentIndex]?.isweight &&
      fieldArrayRenderProps.value[currentIndex]?.issize &&
      fieldArrayRenderProps.value[currentIndex]?.ispolishcolor &&
      fieldArrayRenderProps.value[currentIndex]?.iseverythingasdescription;

    // const isValidApprovalItems =
    //   (currentIndex !== null &&
    //     (fieldArrayRenderProps.value[currentIndex].approval_items?.length ===
    //       0 ||
    //       fieldArrayRenderProps.value[currentIndex].approval_items?.every(
    //         (item: any) =>
    //           item?.type &&
    //           item?.item_id &&
    //           item?.weight &&
    //           item?.qty &&
    //           item?.unit_id,
    //       ))) ??
    //   false;

    const isValidKarigarItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr
          .length === 0 ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr.every(
          (item: any) =>
            item.item_id &&
            item.weight &&
            item.qty &&
            item.unit_id &&
            item.apply_on &&
            item.rate,
        ));
    await fieldArrayRenderProps.formRenderProps.onSubmit(e);
    if (isValidate && isValidKarigarItems && isChecklistApproved) {
      dispatch(closeJadaiReceiptChecklistDialog());
      dispatch(closeDialog());
      dispatch(setJadaiReceiptOrderItemIndex(null));
      dispatch(setJadaiReceiptOrderSave(false));
    }
  };

  const handleCloseChecklist = () => {
    dispatch(closeJadaiReceiptChecklistDialog());
  };

  const handleSelectOrder = () => {
    dispatch(openJadaiReceiptOrderSelectionDialog());
  };

  const handleOpenImageViewDialog = async (itemID: any) => {
    const payload = {
      process_id: PROCESS_ID.jadai,
      item_id: itemID,
    };
    await dispatch(findProductImageByProcess(payload));
    dispatch(setOrderImageViewPayload(payload));
    dispatch(openJadaiReceiptViewImageDialog());
  };

  return (
    <>
      {dialogName === "jadai-receipt-order-entry-dialog" && (
        <AppDialog>
          <div style={{ width: "80vw", position: "relative" }}>
            {loading && <LoadingPanel gridRef={gridRef} />}
            <CustomAppDialog open={JadaiReceiptChecklistDialog}>
              <div
                className="enhanced-checklist-dialog"
                style={{ width: "60vw", minWidth: "300px" }}
              >
                <GridLayout gap={{ rows: 12, cols: 10 }}>
                  {/* Header */}
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingBottom: "12px",
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <Typography.h4
                      style={{
                        margin: "0",
                        color: "#2c3e50",
                        fontWeight: "600",
                      }}
                    >
                      Quality Checklist
                    </Typography.h4>
                    <ImCross
                      className="enhanced-checklist-dialog__close-button"
                      style={{
                        fontSize: 20,
                        color: "white",
                        background: "#dc3545",
                        borderRadius: "50%",
                        padding: "6px",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        dispatch(closeJadaiReceiptChecklistDialog())
                      }
                    />
                  </GridLayoutItem>

                  <div className="w-100">
                    <Typography.h5 style={{ margin: 0 }}>
                      Order Detail - No:
                      {currentIndex !== null &&
                        fieldArrayRenderProps.value?.[currentIndex]?.order_no}
                    </Typography.h5>
                    <table className="table table-striped table-bordered table-hover">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Sub Category</th>
                          <th>Order Date</th>
                          <th>Delivery Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.product_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.category_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.sub_category_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.order_date
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.delivery_date
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <thead>
                        <tr>
                          <th>Estimated Weight (gm)</th>
                          <th>Size</th>
                          <th>Purity</th>
                          <th>Color</th>
                          <th>Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {formatWeight(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.order_estimate_weight,
                              )}
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.size
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.Carat
                              }{" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.purity
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.color
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.priority
                              }
                            </td>
                          </tr>
                        )}

                        {currentIndex !== null && (
                          <tr>
                            <td colSpan={5}>
                              <b>Remarks :</b>{" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.Order_remark
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <Typography.h5 style={{ margin: 0 }}>
                      Jadai Process Detail
                    </Typography.h5>
                    <table className="table table-striped table-bordered table-hover">
                      <thead>
                        <tr>
                          <th>Karigar Name</th>
                          <th>Process Weight</th>
                          <th>Approx work Start Date</th>
                          <th>Received Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.karigar_name
                              }
                            </td>
                            <td>
                              {formatWeight(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.weight,
                              )}
                            </td>
                            <td>
                              {moment(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.given_date,
                                "YYYY-MM-DD",
                              ).format("DD/MM/YYYY")}
                            </td>
                            <td>
                              {moment(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.receive_date,
                                "YYYY-MM-DD",
                              ).format("DD/MM/YYYY")}
                            </td>
                          </tr>
                        )}
                        {currentIndex !== null && (
                          <tr>
                            <td colSpan={5}>
                              <b>Remarks :</b>{" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.issue_remarks
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Checklist Items */}
                  <div style={{ display: "flex", gap: "1vw" }}>
                    <div
                      style={{
                        width: "34vw",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "start",
                        gap: "10px",
                      }}
                    >
                      <div className="w-100">
                        <Typography.h5 style={{ margin: 0 }}>
                          Size Details
                        </Typography.h5>
                        {currentIndex !== null && (
                          <table className="table table-striped table-bordered table-hover">
                            <thead>
                              <tr>
                                <th>Title</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fieldArrayRenderProps.value?.[
                                currentIndex
                              ]?.sizedetails?.map(
                                (item: any, index: number) => (
                                  <tr key={index}>
                                    <td>{item?.title}</td>
                                    <td>{item?.value}</td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>

                      <Button
                        type="button"
                        style={{ textTransform: "uppercase" }}
                        themeColor={"info"}
                        fillMode="solid"
                        disabled={
                          !fieldArrayRenderProps.formRenderProps.valueGetter(
                            `${fieldName}.${currentIndex}.order_no`,
                          )
                        }
                        onClick={() =>
                          handleOpenImageViewDialog(
                            fieldArrayRenderProps.formRenderProps.valueGetter(
                              `${fieldName}.${currentIndex}.item_id`,
                            ),
                          )
                        }
                      >
                        <FaRegEye style={{ fontSize: 24 }} /> View Image
                      </Button>
                      <ViewProductImages />
                    </div>
                    <div
                      className="enhanced-checklist-dialog__items"
                      style={{ width: "25vw" }}
                    >
                      {[
                        {
                          name: `${fieldName}.${currentIndex}.iscaret`,
                          title: "Caret",
                          label: "Caret",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isdesign`,
                          title: "Design",
                          label: "Design",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isgoldcolor`,
                          title: "Gold Color",
                          label: "Gold Color",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isweight`,
                          title: "Weight (gm)",
                          label: "Weight (gm)",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.issize`,
                          title: "Size",
                          label: "Size",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.ispolishcolor`,
                          title: "Polish Color",
                          label: "Polish Color",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.iseverythingasdescription`,
                          title: "Everything as Description",
                          label: "Everything as Description",
                        },
                      ].map((item, index) => (
                        <GridLayoutItem
                          key={item.name}
                          className="enhanced-checklist-dialog__item"
                          style={{
                            padding: "10px 12px",
                            borderRadius: "8px",
                            backgroundColor: "#f2f4ff",
                            marginBottom: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            animationDelay: `${index * 0.1}s`,
                          }}
                        >
                          <Field
                            id={`fieldId-${index}`}
                            name={item.name}
                            title={item.title}
                            label={item.label}
                            size="large"
                            component={FormCheckbox}
                            validator={requiredValidator}
                            wrapperClassName="enhanced-checklist-dialog__checkbox"
                          />
                        </GridLayoutItem>
                      ))}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: "15px",
                      borderTop: "1px solid #e9ecef",
                      gap: "8px",
                    }}
                  >
                    <Field
                      name={`${fieldName}.${currentIndex}.isfinishorder`}
                      title={"Finish Order"}
                      label={"Finish Order"}
                      size="large"
                      component={FormCheckbox}
                      wrapperClassName="enhanced-checklist-dialog__checkbox"
                    />
                    <div>
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        // className="enhanced-checklist-dialog__button"

                        onClick={handleCloseChecklist}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        onClick={(e) => handleCloseAndAdd(e)}
                      >
                        {!jadai_receipt_guid ? "Submit" : "Submit"}
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </div>
            </CustomAppDialog>
            <OrderSelectionDialog
              formRenderProps={fieldArrayRenderProps.formRenderProps}
              fieldname={`${fieldName}.${currentIndex}`}
            />
            <JadaiReceiptWatcher
              formRenderProps={fieldArrayRenderProps.formRenderProps}
              fieldname={`${fieldName}.${currentIndex}`}
            />
            <GridLayout
              style={{
                padding: 10,
              }}
              gap={{ rows: 10, cols: 10 }}
            >
              <GridLayoutItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography.h4>Add Order</Typography.h4>
                <ImCross
                  style={{
                    color: "white",
                    background: "red",
                    borderRadius: "50%",
                    padding: "5px",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleFormClose(currentIndex)}
                />
              </GridLayoutItem>
              <GridLayout cols={[{ width: "100%" }]}>
                <GridLayoutItem
                  style={{
                    minHeight: "30vh",
                    maxHeight: "90vh",
                    border: "2px solid #fba700",
                    borderRadius: "10px",
                    padding: "5px",
                  }}
                >
                  {/* <Card className="fieldarray-card"> */}
                  <GridLayout
                    style={{ marginRight: 30, padding: 10 }}
                    gap={{ rows: 10, cols: 10 }}
                    cols={[
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                    ]}
                  >
                    <GridLayoutItem
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        style={{ width: "fit-content", padding: "5px" }}
                        onClick={handleSelectOrder}
                      >
                        Select Order
                      </Button>
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.order_no`}
                        label="Order No"
                        placeholder="i.e. 1542"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2} rowSpan={2}>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_description`}
                        label="Product Description"
                        placeholder="Write Here..."
                        component={FormTextArea}
                        rows={4}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_category_id`}
                        label="Product Category"
                        placeholder="Select Product Category"
                        disabled={true}
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={ItemCategoryList?.map(
                          (itemcate: IItemCategory) => {
                            return {
                              value: itemcate.ID,
                              label: itemcate.category_name,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_id`}
                        label="Product"
                        placeholder="Select Product"
                        disabled={true}
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={ItemList?.map((item: IItem) => {
                          return {
                            value: item.id,
                            label: item.item_name,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.weight`}
                        label="weight"
                        placeholder="Weight"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    {/* USED BY KARIGAR ITEMS ARRAY */}
                    <GridLayoutItem colSpan={4}>
                      <hr />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        overflowX: "hidden",
                        border: "1px dotted gray",
                        padding: "5px",
                        borderRadius: "8px",
                      }}
                    >
                      <FieldArray
                        formRenderProps={fieldArrayRenderProps.formRenderProps}
                        component={JadaiReceiptUsedByKarigarItemsArray}
                        name={`${fieldName}.${currentIndex}.item_used_by_kariagr`}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={4}>
                      <GridLayout
                        style={{ marginRight: 50 }}
                        gap={{ rows: 10, cols: 10 }}
                        cols={[
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                        ]}
                      >
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.ForOpenSettings`}
                            label="Open Settings"
                            component={FormCheckbox}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem colSpan={6}></GridLayoutItem>
                        {fieldArrayRenderProps.value?.[currentIndex]
                          ?.ForOpenSettings && (
                          <GridLayoutItem>
                            <Field
                              name={`${fieldName}.${currentIndex}.Dank`}
                              label="Dank Weight"
                              placeholder="0.000"
                              format="n3"
                              component={FormNumericTextField}
                              validator={requiredValidator}
                              astrike={true}
                            />
                          </GridLayoutItem>
                        )}
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.gross_weight`}
                            label="Gross Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.stone_weight`}
                            label="Stone Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.net_weight`}
                            label="Net Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            disabled={true}
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.stone_qty`}
                            label="Stone Quantity"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.rate`}
                            label="Rate"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.extra_cost`}
                            label="Extra Cost"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.total`}
                            label="Total"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            disabled={true}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        {/* <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.purity`}
                            label="Purity"
                            placeholder="0.00"
                            format="n2"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.wastage`}
                            label="Wastage"
                            placeholder="0.00"
                            format="n2"
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem> */}
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.kundan`}
                            label="Kundan"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem colSpan={2}>
                          <Field
                            name={`${fieldName}.${currentIndex}.less_jadai_verification`}
                            label="Wax Weight"
                            placeholder="0.00"
                            format="n3"
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem>
                        {/* <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.phine`}
                            label="Phine (gm)"
                            placeholder="0.00"
                            format="n2"
                            disabled={true}
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem> */}
                      </GridLayout>
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        textAlign: "end",
                        marginTop: "20px",
                        marginRight: 20,
                        width: "100%",
                      }}
                    >
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        onClick={(e) => handleAddOrder(e)}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        onClick={() => handleFormClose(currentIndex)}
                      >
                        Cancel
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                  {/* </Card> */}
                </GridLayoutItem>
              </GridLayout>
            </GridLayout>
          </div>
        </AppDialog>
      )}
    </>
  );
};

const ViewProductImages = () => {
  const processByImageList = useAppSelector(
    (state) => state.item.ProcessByImageList,
  );
  const JadaiReceiptViewImageDialog = useAppSelector(
    (state) => state.jadaireceipt.JadaiReceiptViewImageDialog,
  );
  const loading = useAppSelector((state) => state.item.loading);
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const settings = {
    dots: true,
    infinite: true,
    autoplay: false,
    centerMode: true,
    centerPadding: "0px",
    autoplaySpeed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const handleOpenImage = (url: string) => {
    if (url && typeof url === "string") {
      window.open(url);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <CustomAppDialog open={JadaiReceiptViewImageDialog}>
        {/* Image Gallery */}
        <div style={{ width: "35vw", height: "50vh" }}>
          <GridLayout gap={{ rows: 10, cols: 10 }}>
            <GridLayoutItem
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography.h5 style={{ margin: "0" }}>
                {"Product Images"}
              </Typography.h5>
              <ImCross
                style={{
                  color: "white",
                  background: "red",
                  borderRadius: "50%",
                  padding: "5px",
                  fontSize: "24px",
                }}
                onClick={() => {
                  dispatch(closeJadaiReceiptViewImageDialog());
                  dispatch(setOrderImageViewPayload(null));
                }}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              {processByImageList && processByImageList?.length === 0 && (
                <AlertBox alert="No Image Available!" />
              )}
            </GridLayoutItem>
            <GridLayoutItem
              style={{ display: "flex", justifyContent: "center" }}
            >
              <div
                className="slider-container product-view-images"
                style={{ width: "30vw" }}
              >
                {processByImageList && processByImageList.length > 1 ? (
                  <Slider {...settings}>
                    {processByImageList &&
                      processByImageList.length > 0 &&
                      processByImageList.map((image: any, index: number) => (
                        <div key={index}>
                          <div style={{ height: 300 }}>
                            <img
                              src={image.image_name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              onClick={() => handleOpenImage(image.image_name)}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Title
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.title || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Process Name
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.process_name || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Main Image
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.isreadyimage ? "YES" : "NO"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </Slider>
                ) : (
                  <>
                    {processByImageList &&
                      processByImageList.length > 0 &&
                      processByImageList.map((image: any, index: number) => (
                        <div key={index}>
                          <div style={{ height: 300 }}>
                            <img
                              src={image.image_name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              onClick={() => handleOpenImage(image.image_name)}
                            />
                          </div>
                          {/* <Typography.h5 style={{ textAlign: "center" }}>
                                {image.title}
                              </Typography.h5> */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Title
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.title || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Process Name
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.process_name || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Main Image
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.isreadyimage ? "YES" : "NO"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </div>
            </GridLayoutItem>
          </GridLayout>
        </div>
      </CustomAppDialog>
    </>
  );
};

export default JadaiReceiptOrderItemEntryArray;
